// Phase 29.2 — Today's Beat mobile screen (Total / Visited / Pending tabs).
// Phase 29.5.1 · Smart-Route "Next Up" card at the top of the pending list.
//
// UX: minimal, mobile-first, honours PJP. Falls back to SE's default beat when
// there's no PJP for today. GPS accuracy warning banner surfaces the PWA
// honesty flag (Phase 29.1 · PWA_LIMITS.md).
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Compass,
  Info,
  Loader2,
  MapPin,
  PlusCircle,
  Search,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import RetailerDetailsSheet from "@/components/RetailerDetailsSheet";
import OutletColorChip from "@/components/outlets/OutletColorChip";
import OutletColorLegend from "@/components/outlets/OutletColorLegend";
import {
  activeColourIn, cleanLegend, cleanTag, countByColour, matchesColour,
} from "@/lib/outletColors";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TABS = [
  { key: "total", label: "Total" },
  { key: "pending", label: "Pending" },
  { key: "visited", label: "Visited" },
];

// Pure helper — pick the first-nearest pending outlet from a beats/today
// response. Re-exported from _phase29_5_1_helpers so unit tests can import
// without pulling react-router-dom into the module graph.
export { pickNextUp, formatDistance } from "./_phase29_5_1_helpers";
import { pickNextUp, formatDistance } from "./_phase29_5_1_helpers";

// Straight-line distance in metres between two lat/lng points.
function haversineM(la1, ln1, la2, ln2) {
  const R = 6371000, rad = (x) => (x * Math.PI) / 180;
  const dla = rad(la2 - la1), dln = rad(ln2 - ln1);
  const a = Math.sin(dla / 2) ** 2 + Math.cos(rad(la1)) * Math.cos(rad(la2)) * Math.sin(dln / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// An ASM's "joint with SE X" choice needs to survive navigating away and
// back (e.g. after Start Joint Call → Joint Working form → back to Today's
// Beat) — TodayBeat remounts on every visit to the route, so plain React
// state resets to "My beat" each time. Persist it in localStorage, scoped
// to today's date, so it stays put for the rest of the day (an ASM is
// typically joint-working with the same SE for a stretch, sometimes
// switching 2-3 times) but doesn't linger into tomorrow.
//
// The key is scoped by user id (not just a bare "tulsi:joint_se") — this
// device is shared across test logins, and a bare key leaked one ASM's
// "joint with X" choice into whoever logged in next on the same browser.
// An SE who then landed on this page inherited a stale for_user_id that
// wasn't even their own, and the backend correctly 403'd it as outside
// THEIR visibility ("Not visible to you") — confusing, since the SE never
// touched this picker (it's ASM-only) and the beat WAS actually fine.
const JOINT_SE_STORAGE_PREFIX = "tulsi:joint_se:";
function _todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function readStoredJointSe(userId) {
  if (!userId) return "";
  try {
    const raw = JSON.parse(localStorage.getItem(JOINT_SE_STORAGE_PREFIX + userId) || "null");
    if (raw && raw.date === _todayLocal()) return raw.seId || "";
  } catch { /* ignore */ }
  return "";
}
function writeStoredJointSe(userId, seId) {
  if (!userId) return;
  try {
    if (seId) {
      localStorage.setItem(JOINT_SE_STORAGE_PREFIX + userId, JSON.stringify({ date: _todayLocal(), seId }));
    } else {
      localStorage.removeItem(JOINT_SE_STORAGE_PREFIX + userId);
    }
  } catch { /* ignore */ }
}

// A position worse than this (e.g. a desktop browser guessing from the internet address, often
// +-50 km) is useless for "how far is the outlet" — distances and nearest-first sorting are
// switched off instead of showing a made-up figure like 231 km.
const GPS_USABLE_M = 1000;
const gpsIsUsable = (g) => !!g && g.lat != null && g.lng != null && (g.accuracy_m ?? 0) <= GPS_USABLE_M;

export default function TodayBeat() {
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // ASM doing a joint visit with an SE gets an extra shortcut: a real
  // geofenced Start Call plus the Joint Working form, in one tap, instead
  // of manually starting a call and separately finding the form.
  const isASM = user?.role_code === "ASM";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("total");
  const [search, setSearch] = useState("");
  const [gpsWarn, setGpsWarn] = useState(null);
  const [devGps, setDevGps] = useState(null);
  const goodGps = useMemo(() => (gpsIsUsable(devGps) ? devGps : null), [devGps]);
  const [detailsId, setDetailsId] = useState(null);
  // Outlet colour coding — colour-guide filter (a rule id, NO_COLOUR or null).
  const [colourFilter, setColourFilter] = useState(null);
  // ASM joint-working roster — different SE most days, so "today's beat"
  // means "whichever SE I'm out with today", not a fixed assignment.
  const [seList, setSeList] = useState([]);
  // Only ever read/apply the stored pick for an actual ASM — a non-ASM
  // (e.g. an SE, who never sees this picker) must never inherit a joint-SE
  // selection some other account left in this browser's storage.
  const [selectedSeId, setSelectedSeId] = useState(() =>
    user?.role_code === "ASM" ? readStoredJointSe(user.id) : "");

  const chooseSe = useCallback((seId) => {
    setSelectedSeId(seId);
    writeStoredJointSe(user?.id, seId);
  }, [user?.id]);

  useEffect(() => {
    if (!isASM) return;
    // SE,ASM — an ASM can report to another ASM (a sub-ASM structure), not
    // just to an SE's manager, so "my team" for the joint-call picker must
    // include reportee ASMs too, not just SEs.
    api.get("/users", { params: { role_code: "SE,ASM", status: "active", page_size: 200 } })
      .then(({ data: res }) => setSeList((res.items || []).filter((u) => u.id !== user?.id)))
      .catch(() => {});
  }, [isASM, user?.id]);

  const load = useCallback(async (gps) => {
    setLoading(true);
    try {
      const params = gpsIsUsable(gps) ? { lat: gps.lat, lng: gps.lng } : {};
      if (selectedSeId) params.for_user_id = selectedSeId;
      const { data: res } = await api.get("/beats/today", { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [selectedSeId]);

  // Initial + on-return-to-page refetch (Phase 29.5.1 · recompute-on-close).
  useEffect(() => {
    load(devGps);
    // PWA honesty flag — surface poor GPS accuracy up front.
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const g = { lat: p.coords.latitude, lng: p.coords.longitude,
                     accuracy_m: p.coords.accuracy };
        setDevGps(g);
        // Refetch with GPS so smart_route kicks in.
        load(g);
        const acc = Math.round(p.coords.accuracy ?? 0);
        if (acc > GPS_USABLE_M) {
          setGpsWarn(`Your location is not accurate (±${acc >= 1000 ? `${Math.round(acc / 1000)} km` : `${acc} m`}), so distances are not shown. Use your phone with GPS on.`);
        } else if (acc > 50) {
          setGpsWarn(`Your GPS accuracy is ±${acc}m — go outside for a better fix.`);
        } else {
          setGpsWarn(null);
        }
      },
      () => setGpsWarn("Location permission denied — check-ins will fail."),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
    );
    // Keep the position fresh while this screen is open, so the list re-orders itself as
    // the user walks. Battery: NOT a continuous GPS watch — one low-power fix every 2 min,
    // only while the screen is visible, and only applied when moved more than ~50 m.
    const refreshPos = () => {
      if (document.visibilityState !== "visible") return;
      navigator.geolocation.getCurrentPosition(
        (p) => setDevGps((prev) => {
          const g = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy };
          return prev && haversineM(prev.lat, prev.lng, g.lat, g.lng) < 50 ? prev : g;
        }),
        () => {},
        { enableHighAccuracy: false, maximumAge: 60000, timeout: 15000 },
      );
    };
    const wid = setInterval(refreshPos, 2 * 60 * 1000);
    // Refetch on route back (e.g., after closing a visit) so Next Up updates.
    // location.key changes on every history push, incl. nav(-1) back-to-here.
    return () => clearInterval(wid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, location.key]);

  const counts = data?.counts || { total: 0, visited: 0, pending: 0 };
  // Outlet colour coding — /beats/today already carries each row's colour
  // (row.color_tag) and the colour guide (color_legend); an empty guide
  // means the feature is off for this user, so row colours are ignored too.
  const legend = useMemo(() => cleanLegend(data?.color_legend), [data]);
  const tagOf = useCallback(
    (r) => (legend.length ? cleanTag(r.color_tag) : null), [legend]);
  const activeColour = activeColourIn(legend, colourFilter);
  const listed = useMemo(() => {
    const all = data?.outlets || [];
    // A call that was started but not ended ("in_progress") is still pending
    // work — it belongs in Total AND Pending, and goes first so it can be resumed.
    const base = tab === "total" ? all
      : tab === "pending" ? all.filter((r) => r.status === "pending" || r.status === "in_progress")
      : all.filter((r) => r.status === tab);
    // Search by outlet name / code / address / owner / phone (every word must match).
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const hit = (r) => {
      if (!words.length) return true;
      const o = r.outlet || {};
      const hay = [o.name, o.code, o.address, o.owner_name, o.phone].filter(Boolean).join(" ").toLowerCase();
      return words.every((w) => hay.includes(w));
    };
    const found = base.filter(hit).map((r) => {
      const o = r.outlet || {};
      const d = goodGps && o.gps_lat != null && o.gps_lng != null
        ? Math.round(haversineM(goodGps.lat, goodGps.lng, o.gps_lat, o.gps_lng)) : null;
      return { ...r, distance_m: d };
    });
    // Open calls first, then every pending outlet nearest -> farthest from where the
    // user stands NOW (outlets without a saved location last), then visited ones.
    const byDist = (x, y) => (x.distance_m ?? 1e12) - (y.distance_m ?? 1e12);
    return [
      ...found.filter((r) => r.status === "in_progress").sort(byDist),
      ...found.filter((r) => r.status === "pending").sort(byDist),
      ...found.filter((r) => r.status === "visited"),
    ];
  }, [data, tab, search, goodGps]);
  // Guide counts follow the tab + search; the colour filter applies last.
  const colourCounts = useMemo(() => countByColour(listed, tagOf), [listed, tagOf]);
  const rows = useMemo(
    () => (activeColour ? listed.filter((r) => matchesColour(tagOf(r), activeColour)) : listed),
    [listed, tagOf, activeColour]);

  // Next Up = the nearest pending outlet from where the user stands now (live GPS),
  // falling back to the server's pick when the phone has no fix yet.
  // Resuming an open call is only allowed from the outlet itself (same fence as Start
  // Call): read the phone's location now and let the server check the distance.
  const resumeCall = async (r) => {
    const go = () => nav(`/field/retailer/${r.outlet.id}/dashboard?visit_id=${r.visit_id}`);
    if (!navigator.geolocation) { toast.error("This phone can't give its location, so the call can't be resumed."); return; }
    const t = toast.loading("Checking your location…");
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        try {
          await api.post(`/visits/${r.visit_id}/resume-check`, {
            gps: { lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy } });
          toast.dismiss(t); go();
        } catch (e) {
          toast.dismiss(t);
          const d = e?.response?.data?.detail;
          if (e?.response?.status === 428 && d?.error === "outside_geo_fence") {
            toast.error(`You are ${d.distance_m} m from ${r.outlet.name}. Go to the outlet (within ${d.radius_m} m) to resume this call.`);
          } else toast.error(formatApiError(e));
        }
      },
      () => { toast.dismiss(t); toast.error("Could not read your location. Turn on location and try again."); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  const nextUp = useMemo(() => {
    if (goodGps) {
      let best = null;
      for (const r of data?.outlets || []) {
        const o = r.outlet || {};
        if (r.status !== "pending" || o.gps_lat == null || o.gps_lng == null) continue;
        const d = Math.round(haversineM(goodGps.lat, goodGps.lng, o.gps_lat, o.gps_lng));
        if (!best || d < best.distance_m) best = { outlet: o, distance_m: d, distance_kind: "haversine_straight_line" };
      }
      if (best) return best;
    }
    return pickNextUp(data);
  }, [data, goodGps]);

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="today-beat-screen">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-400">
            Today's beat
          </div>
          <h1 className="text-2xl font-extrabold text-neutral-900">
            {data?.beat?.name || "No beat assigned"}
          </h1>
          <div className="text-xs font-mono text-neutral-500">
            {data?.date || ""} {data?.pjp_id ? "· PJP scheduled" : "· fallback beat"}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => nav("/outlets/new?from=new-call")}
          className="h-9 px-3 rounded-lg border-[#6D2C91]/30 text-[#6D2C91] font-medium shrink-0"
          data-testid="today-beat-add-new-call"
        >
          <PlusCircle className="h-4 w-4 mr-1.5" /> New Call
        </Button>
      </div>

      {isASM && seList.length > 0 && (
        <div className="flex items-center gap-2" data-testid="today-beat-se-picker">
          <Users className="h-4 w-4 text-emerald-700 shrink-0" />
          <Select
            value={selectedSeId || "__mine__"}
            onValueChange={(v) => chooseSe(v === "__mine__" ? "" : v)}
          >
            <SelectTrigger className="h-9 text-sm" data-testid="today-beat-se-select">
              <SelectValue placeholder="My beat" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__mine__">My beat</SelectItem>
              {seList.map((se) => (
                <SelectItem key={se.id} value={se.id}>
                  Joint with {se.name || se.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {isASM && selectedSeId && (
        <div className="text-xs px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800"
             data-testid="today-beat-joint-mode-banner">
          Viewing {seList.find((s) => s.id === selectedSeId)?.name || "SE"}'s beat for
          today — tap <b>Joint call</b> on an outlet to punch your own geofenced visit
          there and open the Joint Working form.
        </div>
      )}

      {gpsWarn && (
        <div
          className="text-xs px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800"
          data-testid="today-beat-gps-warn"
        >
          {gpsWarn}
        </div>
      )}

      {/* Phase 29.5.1 · Smart Route "Next Up" — hidden when no pending outlets */}
      {nextUp && (
        <NextUpCard
          nextUp={nextUp}
          onStart={selectedSeId ? null : () => nav(`/visits/start/${nextUp.outlet.id}`)}
          onJointCall={isASM ? () => nav(`/visits/start/${nextUp.outlet.id}?joint=1`) : null}
          onDetails={() => setDetailsId(nextUp.outlet.id)}
        />
      )}

      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-neutral-400" />
        <input
          type="search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search outlet by name, code or address…"
          className="w-full h-11 rounded-xl border border-neutral-200 bg-white pl-9 pr-9 text-sm outline-none focus:border-[#6D2C91]"
          data-testid="today-beat-search"
        />
        {search && (
          <button type="button" onClick={() => setSearch("")} aria-label="Clear search"
                  className="absolute right-2 top-1.5 h-8 w-8 text-xl leading-none text-neutral-400">×</button>
        )}
      </div>

      <OutletColorLegend
        legend={legend}
        counts={colourCounts}
        active={activeColour}
        onChange={setColourFilter}
        testid="today-beat-color-legend"
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 w-full">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              data-testid={`today-beat-tab-${t.key}`}
            >
              {t.label} ({counts[t.key] ?? (t.key === "total" ? counts.total : 0)})
            </TabsTrigger>
          ))}
        </TabsList>
        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-3 space-y-2">
            {loading && (
              <div className="py-6 text-center text-neutral-400">
                <Loader2 className="h-5 w-5 inline animate-spin" />
              </div>
            )}
            {!loading && rows.length === 0 && (
              <div className="py-10 text-center text-neutral-400 text-sm">
                {search.trim() && activeColour ? `No outlet with this colour matches "${search.trim()}". Tap All to see every outlet.`
                  : search.trim() ? `No outlet matches "${search.trim()}".`
                  : activeColour ? "No outlet with this colour here." : "Nothing here."}
              </div>
            )}
            {rows.map((r) => (
              <OutletRow
                key={r.outlet.id}
                row={r}
                colorTag={tagOf(r)}
                distance_m={r.distance_m}
                onStart={selectedSeId ? null : () => nav(`/visits/start/${r.outlet.id}`)}
                onResume={selectedSeId || !r.visit_id ? null : () => resumeCall(r)}
                onJointCall={isASM ? () => nav(`/visits/start/${r.outlet.id}?joint=1`) : null}
                // A "Visited" row had no way back into the retailer dashboard
                // at all — the only place "Reopen call" (with a lookup-driven
                // reason) actually lives. Tapping the row now opens it. Not
                // wired when browsing an SE's beat — that's the SE's own
                // visit to reopen, not the ASM's.
                onOpenVisited={selectedSeId ? null : () => nav(`/field/retailer/${r.outlet.id}/dashboard`)}
                onDetails={() => setDetailsId(r.outlet.id)}
              />
            ))}
          </TabsContent>
        ))}
      </Tabs>

      <RetailerDetailsSheet
        retailerId={detailsId}
        open={!!detailsId}
        onOpenChange={(v) => !v && setDetailsId(null)}
      />
    </div>
  );
}

function NextUpCard({ nextUp, onStart, onJointCall, onDetails }) {
  const distanceLabel = formatDistance(nextUp.distance_m, nextUp.distance_kind);
  return (
    <Card
      className="border-emerald-200 rounded-2xl bg-emerald-50/50"
      data-testid="beat-next-up-card"
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 h-8 w-8 rounded-full bg-emerald-600/10 flex items-center justify-center flex-shrink-0">
            <Compass className="h-4 w-4 text-emerald-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
              Next up · nearest pending
            </div>
            <div
              className="text-sm font-extrabold text-neutral-900 truncate"
              data-testid="beat-next-up-outlet-name"
            >
              {nextUp.outlet.name}
            </div>
            <div className="text-[11px] text-neutral-500 truncate flex items-center gap-1 mt-0.5">
              <MapPin className="h-3 w-3 inline flex-shrink-0" />
              <span className="truncate">{nextUp.outlet.address || "—"}</span>
            </div>
            <div
              className="text-[11px] font-mono text-emerald-800 mt-1"
              data-testid="beat-next-up-distance"
            >
              {distanceLabel}
            </div>
          </div>
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            {onStart && (
              <Button
                size="sm"
                onClick={onStart}
                className="tulsi-gradient text-white h-9 px-3 rounded-lg"
                data-testid="beat-next-up-cta"
              >
                Start <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
            {onJointCall && (
              <Button
                size="sm"
                variant="outline"
                onClick={onJointCall}
                className="h-8 px-3 rounded-lg border-emerald-300 text-emerald-700 text-[11px]"
                data-testid="beat-next-up-joint-cta"
              >
                <Users className="h-3.5 w-3.5 mr-1" /> Joint call
              </Button>
            )}
            <button
              type="button"
              onClick={onDetails}
              className="h-6 w-full flex items-center justify-center rounded-md text-neutral-500 hover:text-[#6D2C91] hover:bg-neutral-100"
              data-testid="beat-next-up-details"
              aria-label="Retailer details"
            >
              <Info className="h-4 w-4" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function OutletRow({ row, colorTag, distance_m, onStart, onResume, onJointCall, onOpenVisited, onDetails }) {
  const done = row.status === "visited";
  const inProgress = row.status === "in_progress";
  return (
    <Card
      className={"rounded-2xl " + (inProgress ? "border-amber-300 bg-amber-50/60" : "border-neutral-200")
        + (colorTag ? " border-l-4" : "")}
      style={colorTag ? { borderLeftColor: colorTag.color } : undefined}
      data-testid={`today-beat-row-${row.outlet.id.slice(0, 8)}`}
    >
      <CardContent className="p-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-neutral-400">
            {row.outlet.code}
            {inProgress && <span className="ml-2 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-semibold">Call in progress</span>}
          </div>
          <div className="text-sm font-semibold text-neutral-900 truncate">
            {row.outlet.name}
          </div>
          {colorTag && (
            <div className="mt-0.5">
              <OutletColorChip tag={colorTag} testid={`today-beat-color-${row.outlet.id.slice(0, 8)}`} />
            </div>
          )}
          <div className="text-[11px] text-neutral-500 mt-1 flex items-center gap-2">
            <MapPin className="h-3 w-3" />
            <span className="truncate">{row.outlet.address || "—"}</span>
            {row.status !== "visited" && distance_m != null && (
              <span className="shrink-0 font-semibold text-emerald-700" data-testid={`today-beat-dist-${row.outlet.id.slice(0, 8)}`}>
                · {distance_m >= 1000 ? `${(distance_m / 1000).toFixed(1)} km` : `${distance_m} m`}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onDetails}
          className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full text-neutral-400 hover:text-[#6D2C91] hover:bg-neutral-100"
          data-testid={`today-beat-details-${row.outlet.id.slice(0, 8)}`}
          aria-label="Retailer details"
        >
          <Info className="h-4 w-4" />
        </button>
        <div className="flex flex-col gap-1 items-stretch">
          {done ? (
            onOpenVisited ? (
              // Tappable — this is the only way in to the retailer dashboard's
              // "Reopen call" action (requires a lookup-driven reason) once a
              // call is already marked Visited today.
              <button
                type="button"
                onClick={onOpenVisited}
                className="text-green-700 text-[11px] font-semibold flex items-center gap-1 rounded-md px-1 py-1 -m-1 hover:bg-green-50"
                data-testid={`today-beat-done-${row.outlet.id.slice(0, 8)}`}
              >
                <CheckCircle2 className="h-4 w-4" /> Visited
                <ArrowRight className="h-3 w-3 ml-0.5 opacity-60" />
              </button>
            ) : (
              <div className="text-green-700 text-[11px] font-semibold flex items-center gap-1"
                   data-testid={`today-beat-done-${row.outlet.id.slice(0, 8)}`}>
                <CheckCircle2 className="h-4 w-4" /> Visited
              </div>
            )
          ) : inProgress && onResume ? (
            <Button
              size="sm"
              onClick={onResume}
              className="bg-amber-500 hover:bg-amber-600 text-white h-9 px-3 rounded-lg"
              data-testid={`today-beat-resume-${row.outlet.id.slice(0, 8)}`}
            >
              Resume call <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          ) : (
            onStart && (
              <Button
                size="sm"
                onClick={onStart}
                className="tulsi-gradient text-white h-9 px-3 rounded-lg"
                data-testid={`today-beat-start-${row.outlet.id.slice(0, 8)}`}
              >
                Start <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )
          )}
          {/* An ASM's joint call is its own separate geofenced visit record —
              punch it regardless of whether the SE already visited this
              outlet today. Previously this button vanished the moment the
              row showed "Visited", so an ASM could never joint-call an
              outlet the SE had already been to. */}
          {onJointCall && (
            <Button
              size="sm"
              variant="outline"
              onClick={onJointCall}
              className="h-7 px-3 rounded-lg border-emerald-300 text-emerald-700 text-[10px]"
              data-testid={`today-beat-joint-${row.outlet.id.slice(0, 8)}`}
            >
              <Users className="h-3 w-3 mr-1" /> Joint call
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
